import { useEffect, useRef, useState } from 'react'
import { Platform } from 'react-native'

import { useToast } from '@zet/common/components/toast'
import webDocumentUpload from '@zet/common/utils/webDocumentUpload'
import * as DocumentPicker from 'expo-document-picker'

import useUploadLoanDocumentToS3 from '../../api/uploadLoanDocumentToS3/hooks'
import { brandsI18n } from '../../localization'
import { UserDetailsProps } from '../../types'

const usePlDocumentPicker = (
  code: string,
  userDetails: UserDetailsProps,
  onChange: (url: string) => null,
  placeholder: string | null,
  config: {
    required: boolean
  }
) => {
  const [selectedDocument, setSelectedDocument] = useState(null)
  const [isUploading, setIsUploading] = useState(false)
  const [isUploaded, setIsUploaded] = useState(false)
  const [documentName, setDocumentName] = useState(null)
  const toast = useToast()
  useEffect(() => {
    if (placeholder) {
      setIsUploaded(true)
      const decodedName = decodeURIComponent(placeholder)
      setDocumentName(decodedName)
    }
  }, [placeholder])

  const { mutate, progress: uploadProgress } = useUploadLoanDocumentToS3()
  const uploadIntervalRef = useRef(null)

  const removeDoc = (fieldId: string, isCancelled: boolean) => {
    if (isCancelled) {
      if (uploadIntervalRef.current) {
        clearInterval(uploadIntervalRef.current)
        uploadIntervalRef.current = null
      }
      onChange('')
      setSelectedDocument(null)
      setDocumentName(null)
      setIsUploaded(false)
      setIsUploading(false)
    }
  }

  const getBlob = async (fileUri: string) => {
    const resp = await fetch(fileUri)
    const imageBody = await resp.blob()
    return imageBody
  }
  const pickDocument = async (fieldId: string) => {
    let result, cancelled, webDocumentResponse
    try {
      if (Platform.OS === 'web') {
        webDocumentResponse = await webDocumentUpload.getDocumentAsync({
          type: ['image/jpeg', 'image/png', 'application/pdf'],
          multiple: false,
        })
        cancelled = webDocumentResponse?.canceled
        result = webDocumentResponse?.assets?.[0]
      } else {
        result = await DocumentPicker.getDocumentAsync()
        cancelled = result?.canceled
      }
      if (!cancelled) {
        if (
          result?.mimeType !== 'application/pdf' &&
          result?.mimeType !== 'image/png' &&
          result?.mimeType !== 'image/jpeg' &&
          result?.mimeType !== 'image/jpg'
        ) {
          toast?.show({
            intent: 'error',
            heading: brandsI18n.t('toastMessages.error'),
            description: 'Please upload a PDF, PNG, or JPEG file',
            hideAfter: 3000,
          })
        } else if (result?.size > 2097152) {
          toast?.show({
            intent: 'error',
            heading: brandsI18n.t('toastMessages.error'),
            description: 'Please upload document less than 2 MB',
            hideAfter: 3000,
          })
        } else {
          if (fieldId) {
            setIsUploading(true)
            setIsUploaded(false)
            setSelectedDocument(result?.uri)
            setDocumentName(result?.name)
            const formData = new FormData()
            const docBlob = await getBlob(result?.uri)
            formData?.append('file', docBlob)
            formData?.append(
              'ocFieldValueDto',
              JSON.stringify({
                fieldCode: Number(fieldId),
              })
            )
            mutate(
              {
                customerId: userDetails?.customerId,
                file: formData,
                ocFieldCode: code,
                onecode: userDetails?.onecode,
                partnerId: userDetails?.partnerId,
                fileName: result?.name,
              },
              {
                onSuccess(data: any) {
                  const s3Url = data?.response?.data?.data?.message
                  onChange(s3Url)
                  setIsUploading(false)
                  setIsUploaded(true)
                },
                onError() {
                  toast?.show({
                    intent: 'error',
                    heading: brandsI18n.t('toastMessages.error'),
                    description: 'Something went wrong',
                    hideAfter: 3000,
                  })
                  setIsUploading(false)
                  setIsUploaded(false)
                },
              }
            )
          }
        }
      } else {
        setSelectedDocument(null)
        setDocumentName(null)
        setIsUploaded(false)
        setIsUploading(false)
      }
    } catch (error) {
      toast?.show({
        intent: 'error',
        heading: brandsI18n.t('toastMessages.error'),
        description: 'Something went wrong',
        hideAfter: 3000,
      })
    }
  }
  return {
    selectedDocument,
    pickDocument,
    documentName,
    setDocumentName,
    isUploading,
    isUploaded,
    removeDoc,
    uploadProgress,
  }
}
export { usePlDocumentPicker }
